import { z } from "zod";
import FoodsApi from "@/packages/api/Api/V1/FoodsApi";
import { jsonHeaders } from "@/packages/dashboard/csrf";
import { withAppDomain } from "@/packages/dashboard/utils/url-utils";
import { invalidateFoodsAutocompleteQueries } from "@/packages/dashboard/query-client";

// Zod schema for the request payload
const createFoodItemPayloadSchema = z.object({
  food_item: z.object({
    name: z.string(),
    description: z.string().nullable(),
    serving_description: z.string().nullable(),
    serving_unit: z.string(),
    serving_size: z.number(),
    calories: z.number(),
    proteins: z.number(),
    carbs: z.number(),
    fats: z.number(),
  }),
});

// Zod schema for the successful API response
const createFoodItemResponseSchema = z.object({
  id: z.number(),
});

export type CreateFoodItemPayload = z.infer<typeof createFoodItemPayloadSchema>;
export type CreateFoodItemResponse = z.infer<typeof createFoodItemResponseSchema>;

export async function createFoodItem(payload: CreateFoodItemPayload): Promise<CreateFoodItemResponse> {
  const url = withAppDomain(FoodsApi.create.path());
  const response = await fetch(url, {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { errors?: string[] } | null;
    const message = body?.errors?.join(", ") || `Unable to create food item (status ${response.status}).`;
    throw new Error(message);
  }

  const data = await response.json();
  const parsed = createFoodItemResponseSchema.parse(data);

  await invalidateFoodsAutocompleteQueries();

  return parsed;
}
